import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileUpload } from '@/components/FileUpload';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Star, Briefcase, Clock, ExternalLink, ArrowLeft, 
  DollarSign, Shield, Loader2, Zap, Bot, BadgeCheck, Eye, X, RefreshCw, Lightbulb, Upload, Mic
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOMMY_IMAGE = "https://images.unsplash.com/photo-1762029027344-e100f27b65f9?crop=entropy&cs=srgb&fm=jpg&w=100";

const defaultAvatars = [
  "https://images.unsplash.com/photo-1673255745677-e36f618550d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1625314897518-bb4fe6e95229?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200",
  "https://images.unsplash.com/photo-1767716134895-296cbd7aec1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxyb2JvdCUyMGFpJTIwYXZhdGFyJTIwZnV0dXJpc3RpY3xlbnwwfHx8fDE3NzM0NDEyNDF8MA&ixlib=rb-4.1.0&q=85&w=200"
];

const getAvatarForAgent = (agentId) => {
  const hash = agentId ? agentId.charCodeAt(0) + agentId.charCodeAt(agentId.length - 1) : 0;
  return defaultAvatars[hash % defaultAvatars.length];
};

export default function AgentDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [instantHireModalOpen, setInstantHireModalOpen] = useState(false);
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleData, setSampleData] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);
  const [myJobs, setMyJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [hours, setHours] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [imgError, setImgError] = useState(false);
  
  // Instant hire form state
  const [jobDescription, setJobDescription] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [wordCount, setWordCount] = useState(500);
  const [tone, setTone] = useState('professional');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const response = await axios.get(`${API}/agents/${id}`);
        setAgent(response.data);
        
        // Check if we should open instant hire modal
        if (searchParams.get('hire') === 'instant' && response.data.is_house_agent) {
          setInstantHireModalOpen(true);
        }
      } catch (error) {
        toast.error('Failed to load agent');
        navigate('/agents');
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id, navigate, searchParams]);

  useEffect(() => {
    const fetchMyJobs = async () => {
      if (user && user.user_type === 'client') {
        try {
          const response = await axios.get(`${API}/my-jobs`, {
            headers: getAuthHeaders()
          });
          setMyJobs(response.data.filter(j => j.status === 'open'));
        } catch (error) {
          console.error('Error fetching jobs:', error);
        }
      }
    };
    fetchMyJobs();
  }, [user, getAuthHeaders]);

  const handleHire = async () => {
    if (!selectedJob) {
      toast.error('Please select a job');
      return;
    }
    if (hours < 1) {
      toast.error('Please enter valid hours');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        agent_id: id,
        job_id: selectedJob,
        hours: parseInt(hours),
        origin_url: window.location.origin
      }, {
        headers: getAuthHeaders()
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  const handleInstantHire = async () => {
    if (!jobDescription.trim()) {
      toast.error('Please describe what you need');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/house-agents/hire/${id}`, {
        job_description: jobDescription,
        additional_context: additionalContext,
        word_count: parseInt(wordCount),
        tone: tone,
        origin_url: window.location.origin
      }, {
        headers: getAuthHeaders()
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(errorMsg);
      setProcessing(false);
    }
  };

  const handleViewSample = async () => {
    setLoadingSample(true);
    setSampleModalOpen(true);
    try {
      const response = await axios.get(`${API}/house-agents/sample/${id}`);
      setSampleData(response.data.sample);
    } catch (error) {
      toast.error('Failed to load sample');
      setSampleModalOpen(false);
    } finally {
      setLoadingSample(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!agent) return null;

  const avatarUrl = (!imgError && agent.avatar_url) ? agent.avatar_url : getAvatarForAgent(agent.id);
  const baseAmount = agent.hourly_rate * hours;
  const commissionRate = user?.is_subscriber ? 0.04 : 0.15;
  const platformFee = baseAmount * commissionRate;
  const totalAmount = baseAmount + platformFee;

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="agent-detail-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Back button */}
        <button 
          onClick={() => navigate('/agents')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-btn"
          aria-label="Go back to agents list"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Agents
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
            >
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="w-24 h-24 rounded-2xl overflow-hidden border border-white/10 flex-shrink-0 bg-zinc-800">
                  {imgError ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500/20 to-cyan-500/20">
                      <Bot className="w-12 h-12 text-cyan-400" />
                    </div>
                  ) : (
                    <img 
                      src={avatarUrl} 
                      alt={agent.name} 
                      className="w-full h-full object-cover"
                      onError={() => setImgError(true)}
                    />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-start gap-3 mb-3">
                    <h1 className="text-2xl md:text-3xl font-bold text-white">{agent.name}</h1>
                    {agent.is_verified && (
                      <Badge 
                        variant="outline" 
                        className="border-cyan-500/50 text-cyan-400 bg-cyan-500/10 flex items-center gap-1.5"
                      >
                        <img src={TOMMY_IMAGE} alt="" className="w-4 h-4 rounded-full" aria-hidden="true" />
                        Tommy Approved
                        <BadgeCheck className="w-4 h-4" aria-hidden="true" />
                      </Badge>
                    )}
                    <Badge 
                      variant="outline" 
                      className={`${agent.status === 'available' ? 'border-green-500/50 text-green-400 bg-green-500/10' : 'border-yellow-500/50 text-yellow-400 bg-yellow-500/10'}`}
                    >
                      {agent.status}
                    </Badge>
                  </div>
                  <p className="text-zinc-400 mb-4">by {agent.owner_name}</p>
                  <div className="flex flex-wrap items-center gap-6 text-sm">
                    <div className="flex items-center gap-1 text-yellow-400">
                      <Star className="w-4 h-4 fill-current" aria-hidden="true" />
                      <span className="font-medium">{agent.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-zinc-400">
                      <Briefcase className="w-4 h-4" aria-hidden="true" />
                      <span>{agent.total_jobs || 0} jobs completed</span>
                    </div>
                    <Badge variant="outline" className="border-indigo-500/50 text-indigo-400 bg-indigo-500/10">
                      {agent.category}
                    </Badge>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
            >
              <h2 className="text-xl font-semibold text-white mb-4">About this Agent</h2>
              <p className="text-zinc-400 leading-relaxed">{agent.description}</p>
            </motion.div>

            {/* Capabilities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
            >
              <h2 className="text-xl font-semibold text-white mb-4">Capabilities</h2>
              <div className="flex flex-wrap gap-3">
                {agent.capabilities?.map((cap, idx) => (
                  <span 
                    key={idx} 
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-zinc-300 text-sm"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Demo Link */}
            {agent.demo_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
              >
                <h2 className="text-xl font-semibold text-white mb-4">Demo</h2>
                <a 
                  href={agent.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Try the demo
                </a>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`sticky top-24 bg-zinc-900/50 border ${agent.is_house_agent ? 'border-yellow-500/30' : 'border-white/10'} rounded-2xl p-6`}
            >
              {/* House Agent Special Header */}
              {agent.is_house_agent && (
                <div className="mb-4 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-2 justify-center">
                    <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                      <Zap className="w-3 h-3 inline mr-1" />
                      Instant AI Work
                    </div>
                  </div>
                  <p className="text-xs text-zinc-400 text-center mt-2">
                    TripleTC House Agent - Work delivered instantly
                  </p>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-white mb-1">
                  ${agent.hourly_rate}
                  <span className="text-lg text-zinc-500 font-normal">{agent.is_house_agent ? '' : '/hour'}</span>
                </div>
                {agent.is_house_agent ? (
                  <p className="text-sm text-yellow-400">One-time payment • Instant delivery</p>
                ) : user?.is_subscriber ? (
                  <p className="text-sm text-yellow-400">+ 4% platform fee ★ Premium</p>
                ) : (
                  <p className="text-sm text-zinc-500">+ 15% platform fee</p>
                )}
              </div>

              {/* House Agent: Instant Hire Flow */}
              {agent.is_house_agent ? (
                <>
                  {/* See Sample Button */}
                  <Button
                    onClick={handleViewSample}
                    variant="outline"
                    className="w-full mb-3 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    data-testid="see-sample-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    See Sample Output
                  </Button>
                  
                  {user ? (
                    <Button
                      onClick={() => setInstantHireModalOpen(true)}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold rounded-none h-12"
                      data-testid="instant-hire-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Instant Hire - ${agent.hourly_rate}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate('/auth?mode=register&type=client')}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold rounded-none h-12"
                      data-testid="signup-instant-hire-btn"
                    >
                      Become a Patron to Hire Instantly
                    </Button>
                  )}
                </>
              ) : (
                /* Regular Agent: Standard Hire Flow */
                <>
                  {user && user.user_type === 'client' ? (
                    <Button
                      onClick={() => setHireModalOpen(true)}
                      className="w-full bg-cyan-500 hover:bg-cyan-600 text-white rounded-none h-12 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all"
                      data-testid="hire-agent-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Hire Agent
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate('/auth?mode=register&type=client')}
                      className="w-full bg-cyan-500 hover:bg-cyan-600 text-white rounded-none h-12"
                      data-testid="signup-to-hire-btn"
                    >
                      Become a Patron to Hire
                    </Button>
                  )}
                </>
              )}

              <div className="mt-6 space-y-4">
                {agent.is_house_agent ? (
                  <>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span>AI-powered instant delivery</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <Shield className="w-4 h-4 text-green-400" />
                      <span>Secure Stripe payment</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <DollarSign className="w-4 h-4 text-cyan-400" />
                      <span>Card & Google Pay accepted</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <Shield className="w-4 h-4 text-green-400" />
                      <span>Secure payment with escrow</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <Clock className="w-4 h-4 text-cyan-400" />
                      <span>Fast response time</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <DollarSign className="w-4 h-4 text-yellow-400" />
                      <span>Card, Google Pay & Crypto accepted</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Hire Modal */}
      <Dialog open={hireModalOpen} onOpenChange={setHireModalOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Hire {agent.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select a job and specify the hours needed
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            <div>
              <Label htmlFor="job-select" className="text-zinc-300 mb-2 block">Select Job</Label>
              {myJobs.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-zinc-500 mb-3">You have no open jobs</p>
                  <Button
                    onClick={() => navigate('/post-job')}
                    variant="outline"
                    className="border-white/20 text-zinc-300"
                  >
                    Create a Job First
                  </Button>
                </div>
              ) : (
                <select
                  id="job-select"
                  value={selectedJob}
                  onChange={(e) => setSelectedJob(e.target.value)}
                  className="w-full h-10 px-3 bg-zinc-800 border border-white/10 rounded-md text-white focus:border-cyan-500/50 outline-none"
                  data-testid="job-select"
                  aria-required="true"
                  aria-label="Select the job you want to assign this agent to"
                >
                  <option value="">Select a job...</option>
                  {myJobs.map(job => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <Label htmlFor="hours-input" className="text-zinc-300 mb-2 block">Hours Needed</Label>
              <Input
                id="hours-input"
                type="number"
                min="1"
                value={hours}
                onChange={(e) => setHours(Math.max(1, parseInt(e.target.value) || 1))}
                className="bg-zinc-800 border-white/10"
                data-testid="hours-input"
                aria-required="true"
                aria-label="Number of hours you need this agent"
              />
            </div>

            {/* Price Breakdown */}
            <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Base ({hours}h × ${agent.hourly_rate})</span>
                <span className="text-white">${baseAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">
                  Platform fee ({user?.is_subscriber ? '4%' : '15%'})
                  {user?.is_subscriber && <span className="text-yellow-400 ml-1">★ Premium</span>}
                </span>
                <span className="text-white">${platformFee.toFixed(2)}</span>
              </div>
              {!user?.is_subscriber && (
                <div className="text-xs text-yellow-400/80 pt-1">
                  💡 Subscribe for $10/mo to pay only 4% fee
                </div>
              )}
              <div className="border-t border-white/10 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-zinc-300 font-medium">Total</span>
                  <span className="text-cyan-400 font-bold text-lg">${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleHire}
              disabled={processing || !selectedJob || myJobs.length === 0}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white h-12 rounded-none"
              data-testid="confirm-hire-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Pay Now
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 text-center mt-3">
              Card, Google Pay, or Crypto (ETH, BTC, SOL, XRP)
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Instant Hire Modal (for House Agents) */}
      <Dialog open={instantHireModalOpen} onOpenChange={setInstantHireModalOpen}>
        <DialogContent className="bg-zinc-900 border-yellow-500/30 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Instant Hire: {agent.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Describe what you need and get AI-powered work delivered instantly
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-4">
            <div>
              <Label htmlFor="job-description" className="text-zinc-300 mb-2 block">
                What do you need? <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="job-description"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="e.g., Write a blog post about AI in healthcare, Create social media content for a tech startup..."
                className="bg-zinc-800 border-white/10 min-h-[100px] text-white"
                data-testid="instant-job-description"
              />
            </div>

            <div>
              <Label htmlFor="additional-context" className="text-zinc-300 mb-2 block">
                Additional Context (optional)
              </Label>
              <Textarea
                id="additional-context"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder="Any specific requirements, brand voice, target audience..."
                className="bg-zinc-800 border-white/10 min-h-[60px] text-white"
                data-testid="instant-additional-context"
              />
            </div>

            {/* File Upload Section */}
            <div>
              <Label className="text-zinc-300 mb-2 block flex items-center gap-2">
                <Upload className="w-4 h-4 text-teal-400" />
                Upload Reference Files (optional)
              </Label>
              <FileUpload 
                onFilesChange={setUploadedFiles}
                maxFiles={3}
              />
              {uploadedFiles.length > 0 && (
                <p className="text-xs text-teal-400 mt-2">
                  {uploadedFiles.length} file(s) will be used as context
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="word-count" className="text-zinc-300 mb-2 block">Target Length</Label>
                <select
                  id="word-count"
                  value={wordCount}
                  onChange={(e) => setWordCount(parseInt(e.target.value))}
                  className="w-full h-10 px-3 bg-zinc-800 border border-white/10 rounded-md text-white focus:border-yellow-500/50 outline-none"
                  data-testid="instant-word-count"
                >
                  <option value="250">Short (~250 words)</option>
                  <option value="500">Medium (~500 words)</option>
                  <option value="1000">Long (~1000 words)</option>
                  <option value="2000">Extended (~2000 words)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="tone" className="text-zinc-300 mb-2 block">Tone</Label>
                <select
                  id="tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full h-10 px-3 bg-zinc-800 border border-white/10 rounded-md text-white focus:border-yellow-500/50 outline-none"
                  data-testid="instant-tone"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="formal">Formal</option>
                  <option value="creative">Creative</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-zinc-300 font-medium">Total</p>
                  <p className="text-xs text-zinc-500">One-time payment</p>
                </div>
                <span className="text-2xl font-bold text-yellow-400">${agent.hourly_rate}</span>
              </div>
            </div>

            <Button
              onClick={handleInstantHire}
              disabled={processing || !jobDescription.trim()}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold h-12"
              data-testid="confirm-instant-hire-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Pay & Get Instant Work
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 text-center">
              Secure payment via Stripe • Work delivered after payment
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sample Output Modal */}
      <Dialog open={sampleModalOpen} onOpenChange={setSampleModalOpen}>
        <DialogContent className="bg-zinc-900 border-cyan-500/30 sm:max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              Sample Output: {agent.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              See the quality of work before you hire
            </DialogDescription>
          </DialogHeader>

          {loadingSample ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : sampleData ? (
            <div className="space-y-4 pt-2">
              <div className="bg-zinc-800/50 rounded-lg p-3 border border-white/10">
                <p className="text-xs text-zinc-500 mb-1">Sample Request:</p>
                <p className="text-zinc-300 text-sm">{sampleData.request}</p>
              </div>
              
              <div>
                <p className="text-xs text-zinc-500 mb-2">Sample Output:</p>
                <div 
                  className="bg-zinc-800/50 rounded-lg p-4 max-h-[40vh] overflow-y-auto border border-cyan-500/20 relative"
                  onContextMenu={(e) => e.preventDefault()}
                  style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                >
                  <pre 
                    className="text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed font-sans"
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    {sampleData.output}
                  </pre>
                  <div className="absolute bottom-2 right-2 text-xs text-zinc-600/50 pointer-events-none">
                    TripleTC Sample
                  </div>
                </div>
              </div>

              {/* Revision Feature Highlight */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 border border-cyan-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium text-sm mb-2">2 Free Revisions Included</h4>
                    <p className="text-zinc-400 text-xs mb-3">
                      Not 100% satisfied? Request changes and our AI will refine the output based on your feedback.
                    </p>
                    <div className="space-y-1.5">
                      <p className="text-xs text-zinc-500 flex items-center gap-1.5">
                        <Lightbulb className="w-3 h-3 text-yellow-400" />
                        <span className="text-zinc-400">Example revision requests:</span>
                      </p>
                      <ul className="text-xs text-zinc-400 space-y-1 ml-5">
                        <li>"Make it more formal and add statistics"</li>
                        <li>"Shorten to 500 words and add bullet points"</li>
                        <li>"Change the tone to be more conversational"</li>
                        <li>"Add more specific examples about [topic]"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setSampleModalOpen(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300"
                >
                  Close
                </Button>
                {user ? (
                  <Button
                    onClick={() => {
                      setSampleModalOpen(false);
                      setInstantHireModalOpen(true);
                    }}
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Hire Now - ${agent.hourly_rate}
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/auth?mode=register&type=client')}
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold"
                  >
                    Sign Up to Hire
                  </Button>
                )}
              </div>

              <p className="text-xs text-center text-zinc-500">
                Industry-standard frameworks • Secure Stripe payment
              </p>
            </div>
          ) : (
            <p className="text-zinc-400 text-center py-8">Sample not available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
